/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Signed key-bundle lookup + verification (F12). Closes the
 * silent-recipient-substitution hole in stage 4 of the create-message
 * flow. See `docs/Application_redesign.md` §3 stage 4 and
 * `docs/plan-crypto-redesign.md` §F12.
 *
 * Bundle anatomy:
 *   {
 *     identity     — id of the bundle owner (email/uuid)
 *     keyValue     — ML-KEM-768 public key (the rotatable encryption key)
 *     signingKey   — ML-DSA-65 public key (the long-lived identity anchor)
 *     signingKeyId — server-side row id for the signing key
 *     signature    — ML-DSA-65 signature over `signingMessage(...)`
 *     algorithm    — locked to 'ML-DSA-65'
 *   }
 *
 * The bundle is *self-verifying*: the server ships `signingKey`, the
 * client checks the signature against it, and TOFU cross-checks the
 * fingerprint of `signingKey` against the pinned fingerprint. An
 * attacker who swaps both `keyValue` and `signingKey` produces a
 * fingerprint that misses the pin → `KeyChangedError` from `tofu.ts`.
 *
 * Signing-message canonicalization:
 *   utf8(identity) || keyValue || signingKey
 *
 * Typed-field byte concatenation (mirrors F6 AAD strategy). Backend B2
 * (`app-backend#37`) must use the same encoding for signatures to verify;
 * a comment is left on B2 to call this out.
 */

import { z } from 'zod';
import { base64nopad } from '@scure/base';
import { verify as mldsaVerify } from '../crypto/mldsa.js';

const utf8 = new TextEncoder();

/** Locked signature algorithm. F12 ships ML-DSA-65 only. */
export const BUNDLE_ALGORITHM = 'ML-DSA-65' as const;

/**
 * Identifier the frontend uses to fetch the system's signed key bundle
 * via `fetchSignedBundle(tdFetch, SYSTEM_IDENTITY)`. The string itself
 * is a **backend contract synchronization point** (`app-backend#37` /
 * B2): the server must accept this exact value as the lookup key on
 * `POST/GET /api/key/member/publickey/signed/{identityId}`, returning a
 * bundle whose `identity` field matches.
 *
 * Kept here (not buried in `storyprocessor.js`) so any future caller —
 * test code, routing-sheet builder, audit tooling — uses the same value.
 */
export const SYSTEM_IDENTITY = 'system' as const;

const base64nopadBytes = z.string().transform((s, ctx) => {
  try {
    return base64nopad.decode(s);
  } catch {
    ctx.addIssue({ code: 'custom', message: 'invalid base64nopad encoding' });
    return z.NEVER;
  }
});

/**
 * Wire-shape of a signed key bundle (`.strict()` — unknown fields are
 * rejected). Decoded form yields `Uint8Array` for the key/signature
 * fields via the `base64nopadBytes` transform.
 */
export const SignedKeyBundleSchema = z
  .object({
    identity: z.string().min(1),
    keyValue: base64nopadBytes,
    signingKey: base64nopadBytes,
    signingKeyId: z.string().min(1),
    signature: base64nopadBytes,
    algorithm: z.literal(BUNDLE_ALGORITHM),
  })
  .strict();

export type SignedKeyBundle = z.infer<typeof SignedKeyBundleSchema>;

/**
 * Thrown when the bundle structure is well-formed JSON/Zod but the
 * signature does not verify under the bundled `signingKey`, or when
 * the structure itself is invalid (missing fields, unsupported
 * algorithm, malformed base64).
 */
export class BundleSignatureError extends Error {
  /** The reason the bundle was rejected. Never user-facing. */
  readonly reason: 'invalid-signature' | 'invalid-shape' | 'invalid-algorithm';
  constructor(message: string, reason: BundleSignatureError['reason']) {
    super(message);
    this.name = 'BundleSignatureError';
    this.reason = reason;
  }
}

/** Thrown when the network fetch itself fails or returns non-200. */
export class BundleFetchError extends Error {
  readonly status: number | undefined;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'BundleFetchError';
    this.status = status;
  }
}

/**
 * Build the deterministic byte sequence that `signature` covers.
 * Typed-field byte concatenation: `utf8(identity) || keyValue || signingKey`.
 *
 * No length prefixes are needed because the three fields have fixed
 * boundaries given the algorithm: `identity` is whatever bytes UTF-8
 * encodes, but it's followed by an ML-KEM-768 public key (1184 bytes)
 * and an ML-DSA-65 public key (1952 bytes), both fixed-size. The
 * server signs and the client verifies the same byte concatenation,
 * so as long as both sides agree on the field order the message is
 * unambiguous.
 *
 * Exposed for test use; production callers go through `verifyBundleSignature`.
 */
export const signingMessage = (bundle: SignedKeyBundle): Uint8Array => {
  const idBytes = utf8.encode(bundle.identity);
  const out = new Uint8Array(
    idBytes.length + bundle.keyValue.length + bundle.signingKey.length
  );
  let offset = 0;
  out.set(idBytes, offset);
  offset += idBytes.length;
  out.set(bundle.keyValue, offset);
  offset += bundle.keyValue.length;
  out.set(bundle.signingKey, offset);
  return out;
};

/**
 * Parse an unknown wire payload (typically from `tdFetch().json()`)
 * into a typed `SignedKeyBundle`. Throws `BundleSignatureError` with
 * `reason: 'invalid-shape'` for any Zod failure (missing fields,
 * unknown fields, malformed base64).
 */
export const parseBundle = (raw: unknown): SignedKeyBundle => {
  const result = SignedKeyBundleSchema.safeParse(raw);
  if (!result.success) {
    throw new BundleSignatureError(
      `bundle shape rejected: ${result.error.message}`,
      'invalid-shape'
    );
  }
  return result.data;
};

/**
 * Verify the bundle's ML-DSA-65 signature against its bundled
 * `signingKey`. Does *not* perform TOFU pinning — that's `tofu.ts`'s
 * job. Returns the bundle on success; throws `BundleSignatureError` on
 * failure.
 */
export const verifyBundleSignature = (
  bundle: SignedKeyBundle
): SignedKeyBundle => {
  if (bundle.algorithm !== BUNDLE_ALGORITHM) {
    throw new BundleSignatureError(
      `unsupported bundle algorithm: ${String(bundle.algorithm)}`,
      'invalid-algorithm'
    );
  }
  const ok = mldsaVerify(
    bundle.signingKey,
    signingMessage(bundle),
    bundle.signature
  );
  if (!ok) {
    throw new BundleSignatureError(
      `bundle signature does not verify for identity '${bundle.identity}'`,
      'invalid-signature'
    );
  }
  return bundle;
};

/**
 * Minimal fetch facade matching the `tdFetch` signature, so this
 * module is testable without importing the real fetcher. Production
 * callers pass `tdFetch` from `../tdfetch.js`.
 */
export interface SignedBundleFetcher {
  (path: string, init?: RequestInit): Promise<Response>;
}

/**
 * Fetch a signed key bundle for `identityId` and verify its
 * signature. Returns the parsed-and-verified bundle, or throws.
 *
 * Endpoint contract (B2 / `app-backend#37`):
 *   POST /api/key/member/publickey/signed
 *
 * The path is intentionally hard-coded here so any server change
 * surfaces as a 404 in this single module rather than in callers.
 */
export const fetchSignedBundle = async (
  fetcher: SignedBundleFetcher,
  identityId: string
): Promise<SignedKeyBundle> => {
  const path = `/api/key/member/publickey/signed/${encodeURIComponent(identityId)}`;
  const response = await fetcher(path);
  if (!response.ok) {
    throw new BundleFetchError(
      `bundle fetch failed for '${identityId}': HTTP ${response.status}`,
      response.status
    );
  }
  let raw: unknown;
  try {
    raw = await response.json();
  } catch (err) {
    throw new BundleFetchError(
      `bundle response was not JSON for '${identityId}': ${String(err)}`
    );
  }
  return verifyBundleSignature(parseBundle(raw));
};
